import { CSSResultGroup, TemplateResult, html, nothing } from 'lit';
import { OutlineElement } from '../../base/outline-element/outline-element';
import { customElement, property } from 'lit/decorators.js';
import componentStyles from './se-counter.css.lit';
import { MobileController } from '../../controllers/mobile-controller';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';

/**
 * The SeCounter component
 * @element se-counter
 * slot - cta: Slot for an option CTA link
 */
@customElement('se-counter')
export class SeCounter extends OutlineElement {
  static styles: CSSResultGroup = [componentStyles];
  mobileController = new MobileController(this);

  /**
   * Current count.
   */
  @property({ type: Number })
  current = 0;

  /**
   * Final value to display. ex: 100, $500, 800+.
   */
  @property({ type: String })
  display: string;

  /**
   * Short description text
   */
  @property({ type: String })
  description = '';

  /**
   * duration in ms.
   */
  @property({ type: Number })
  duration = 2000;

  /**
   * Wether or not the counter is scrolled into view. Set by component.
   */
  @property({ type: Boolean })
  visible = false;

  /**
   * Modifier like "k" or "$" to add to the beginning of the display.
   */
  modStart = '';

  /**
   * Modifier like "k" or "$" to add to the end of the display.
   */
  modEnd = '';

  /**
   * Number to count to
   */
  end = 0;

  /**
   * Prevents annimation if prefers-reduced-motion.
   */
  preventAnimation: boolean;

  // Animation Specific Values
  /**
   * amount to increment count per frame
   */
  increment = 1;

  /**
   * current frame
   */
  frame = 0;

  /**
   * MS delay between frames
   */
  delay: number;

  /**
   * total number of frames
   */
  totalFrames: number;

  render(): TemplateResult {
    return html`
      <div class="se-counter">
        <div class="se-counter--wrapper">
          <div class="se-counter--top-content">${this.counterTemplate()}</div>
          <span class="se-counter--description">
            <span class="visually-hidden">${this.display}</span>
            ${unsafeHTML(this.description)}
          </span>
          <slot name="cta"></slot>
        </div>
      </div>
    `;
  }

  firstUpdated() {
    this.parseDisplay();
    this.setPreventAnimation();
    this.setTotalFrames();
    this.setInitialDelay();
    this.setInitialIncrement();
    this.isElementXPercentInViewport();
    window.addEventListener('scroll', this.isElementXPercentInViewport);
  }

  updated() {
    this.animateCounter();
  }

  disconnectedCallback(): void {
    window.removeEventListener('scroll', this.isElementXPercentInViewport);
  }

  setPreventAnimation() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.current = this.end;
      this.preventAnimation = true;
    }
  }

  counterTemplate() {
    return html` ${this.modStart
        ? html`<span class="se-counter--modifier" aria-hidden="true"
            >${this.modStart}</span
          >`
        : nothing}
      <span class="se-counter--count" aria-hidden="true">${this.current}</span>
      ${this.modEnd
        ? html`<span class="se-counter--modifier" aria-hidden="true"
            >${this.modEnd}</span
          >`
        : nothing}`;
  }

  animateCounter() {
    if (!this.visible || this.preventAnimation) {
      return;
    }

    if (this.visible) {
      const timer = setTimeout(() => {
        if (this.current >= this.end) {
          clearTimeout(timer);
        } else {
          this.advanceFrame();
        }
      }, this.delay);
    }
  }

  advanceFrame() {
    if (this.frame === this.totalFrames - 5) {
      this.increment = 1;
      this.current = this.end - 5;
      this.delay = this.delay * 1.5;
    }
    this.frame++;
    this.current += this.increment;
    if (this.current > this.end) {
      this.current = this.end;
    }
  }

  parseDisplay() {
    // The end value for the number counter.
    this.end = Number(this.display.match(/\d+/));

    // Lengths to test and cut out modifiers.
    const lengthOfDisplay = this.display?.length;
    const lengthOfEnd = this.end?.toString()?.length;

    // If there is no modifier, just a number, skip the logic.
    if (lengthOfEnd === lengthOfDisplay) {
      return;
    }

    const indexOfEnd = this.display?.indexOf(this.end?.toString());

    // Cut the modifier off the beginning.
    if (indexOfEnd !== 0) {
      this.modStart = this.display.slice(0, indexOfEnd);
    }

    // Cut the modifier off the end.
    const numberEnd = indexOfEnd + lengthOfEnd;
    if (numberEnd < lengthOfDisplay) {
      this.modEnd = this.display.slice(numberEnd);
    }
  }

  setInitialIncrement() {
    this.increment = Math.max(Math.round(this.end / this.totalFrames), 1);
  }

  setTotalFrames() {
    const base = (this.duration / 1000) * 24;
    if (this.end > base) {
      this.totalFrames = (this.duration / 1000) * 24;
    } else {
      this.totalFrames = this.end;
    }
  }

  setInitialDelay() {
    this.delay = Math.max(Math.round(this.duration / this.totalFrames), 1);
  }

  /**
   * adapted from https://stackoverflow.com/a/51121566
   */
  isElementXPercentInViewport = () => {
    const rect = this.getBoundingClientRect();
    const windowHeight =
      window.innerHeight || document.documentElement.clientHeight;
    const revealPercentage: number = this.mobileController.isMobile ? 30 : 90;
    return (this.visible = !(
      Math.floor(100 - ((rect.top >= 0 ? 0 : rect.top) / +-rect.height) * 100) <
        revealPercentage ||
      Math.floor(100 - ((rect.bottom - windowHeight) / rect.height) * 100) <
        revealPercentage
    ));
  };
}
